// Wizard Step 1 — visual match for bb-traderx strategy wizard step 1:
//   • Asset block: coin (combobox) + timeframe grid + perps/spot toggle, in one block
//   • Section divider <hr> between blocks
//   • Indicators block with sparkline-card grid + advanced/PA pills

import { AlertTriangle, Info } from "lucide-react";
import { useBuilderState } from "../../../state/BuilderState";
import { IndicatorPicker } from "../IndicatorPicker";
import { RiskSection } from "../RiskSection";
import { CoinPicker } from "./CoinPicker";
import { TimeframeGrid } from "./TimeframeGrid";
import { cn } from "@/lib/utils";
import type { MarketType } from "../../../api/types";

export function Step1Setup() {
  const { draft, patch } = useBuilderState();

  // Conditions are positional into the list built from this exact asset + indicator set, so the
  // BuilderState `patch` guard clears all four condition buckets the moment any of these inputs
  // change. Surface that here (only when there's actually something to lose) so the reset isn't a
  // silent surprise when someone returns to Setup to tweak a param after assembling a strategy.
  const hasPickedConditions =
    draft.longCondIndices.length > 0 ||
    draft.shortCondIndices.length > 0 ||
    draft.exitLongCondIndices.length > 0 ||
    draft.exitShortCondIndices.length > 0;

  return (
    <div className="flex flex-col gap-5">
      {hasPickedConditions && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
          <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
          <span>
            You've already picked conditions in the Discover step. Changing the coin, timeframe,
            market, or indicators below will <span className="font-semibold">clear them</span> — the
            conditions are tied to this exact indicator set, so you'll re-pick in Discover.
          </span>
        </div>
      )}

      {/* Asset & timeframe */}
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-bold">Asset & timeframe</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            The instrument and candle size your strategy watches.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_180px] gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 block mb-1.5">
              Coin
            </label>
            <CoinPicker value={draft.coin} onChange={(coin) => patch({ coin })} />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 block mb-1.5">
              Interval
            </label>
            <TimeframeGrid value={draft.interval} onChange={(interval) => patch({ interval })} />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 block mb-1.5">
              Market
            </label>
            <MarketToggle value={draft.marketType} onChange={(marketType) => patch({ marketType })} />
          </div>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* Indicators — IndicatorPicker renders its own section header ("Indicators" + the
          "N active" badge). */}
      <IndicatorPicker />

      <div className="border-t border-border" />

      {/* Risk limits for the backtest simulation. Exits are auto-discovered, not configured here. */}
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-bold">Risk limits <span className="text-muted-foreground font-normal text-sm">· optional</span></h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Your strategy signals when to <span className="font-semibold text-foreground">enter</span>; the
            matching <span className="font-semibold text-foreground">exit conditions are auto-discovered</span>{" "}
            when you scan (or hand-pick them in the Manual tab). These optional stop/target limits shape the
            backtest and the suggested SL/TP in your alerts.
          </p>
        </div>

        {/* Plain-language scope note — the #1 source of confusion was "does this control my live trades?". */}
        <div className="flex items-start gap-2 rounded-xl border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-muted-foreground leading-snug">
          <Info className="size-3.5 mt-0.5 shrink-0 text-blue-400" />
          <span>
            These affect the <span className="font-semibold text-foreground">backtest only</span>. Live alerts
            fire on entry and on the discovered exit conditions; your trading bot decides when to actually act.
          </span>
        </div>

        <RiskSection />
      </div>
    </div>
  );
}

function MarketToggle({ value, onChange }: { value: MarketType; onChange: (v: MarketType) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {(["perps", "spot"] as const).map((mt) => {
        const active = mt === value;
        return (
          <button
            key={mt}
            type="button"
            onClick={() => onChange(mt)}
            className={cn(
              "py-2 px-3 rounded-xl border-2 text-xs font-bold font-mono uppercase tracking-wider transition-all cursor-pointer",
              active
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-card-elevated text-muted-foreground hover:border-primary/50",
            )}
          >
            {mt}
          </button>
        );
      })}
    </div>
  );
}

